import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import Logo from "../Logo";
import DonateButton from "../DonateButton";
import { useLang } from "../../i18n";
import { NAV_LINKS } from "../../data/content";
import { useContent } from "../../lib/useContent";

export default function Header() {
  const { t, lang, toggle } = useLang();
  const c = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const orgName = lang === "hi" ? c.org_name_hi : c.org_name_en;

  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-50 bg-ngo-cream/85 backdrop-blur-xl border-b border-ngo-deep/10 transition-[box-shadow,padding] duration-300 ${
        scrolled ? "shadow-[0_8px_30px_rgba(23,107,58,0.10)]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
        <Link to="/" data-testid="header-logo-link" className={`flex items-center gap-3 transition-all duration-300 ${scrolled ? "py-2" : "py-3.5"}`}>
          <Logo size={scrolled ? 38 : 46} className="transition-all duration-300" />
          <span className="leading-tight">
            <span className={`block font-heading font-bold text-ngo-forest transition-all duration-300 ${scrolled ? "text-base" : "text-lg"}`}>
              {orgName}
            </span>
            <span className="block text-[10px] tracking-widest uppercase text-ngo-leaf">ANANSHA WELFARE SOCIETY</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="मुख्य नेविगेशन">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-ngo-deep bg-ngo-mint" : "text-ngo-ink/75 hover:text-ngo-deep hover:bg-ngo-mint/60"
                }`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="language-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold hover:bg-ngo-mint transition-colors duration-200"
            aria-label="भाषा बदलें / Switch language"
          >
            <Languages size={15} />
            {lang === "hi" ? "EN" : "हिं"}
          </button>
          <DonateButton label={t("nav.donate")} variant="green" size="md" testid="header-donate-btn" className="hidden sm:inline-flex" />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-full text-ngo-deep hover:bg-ngo-mint transition-colors duration-200"
            aria-label="मेनू खोलें"
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-ngo-deep/10 bg-ngo-cream/95 backdrop-blur-xl"
            aria-label="मोबाइल नेविगेशन"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.key} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.04 }}>
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.key}`}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-xl font-medium ${isActive ? "text-ngo-deep bg-ngo-mint" : "text-ngo-ink/80"}`
                    }
                  >
                    {t(`nav.${l.key}`)}
                  </NavLink>
                </motion.div>
              ))}
              <DonateButton label={t("nav.donate")} variant="green" size="lg" testid="mobile-donate-btn" className="mt-2 w-full" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
